import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

// Catalog Fulfillment Scheduling, Phases 1-2 (Sep 2026).
//
// Reads the BACKEND'S availability decision for the catalog and the
// customer's selected fulfillment mode, and — in Phase 2 — the backend's
// list of valid future windows.
//
// It computes NOTHING: no opening hours, no lead time, no slot maths, no
// synthesised future times. Every window the customer can pick came from
// `/catalog/availability`, and checkout revalidates the chosen one
// server-side regardless. This hook exists to tell the customer BEFORE
// they fill in a form, not to decide anything.
//
// Phase 2 scope is Pickup and Takeout. The applicable modes come from the
// response (`applies_to_modes`) rather than being hard-coded, so widening
// scope later needs no frontend release.
//
// Gate C6 (Sep 2026) adds scheduled Uber DELIVERY, reported by the
// backend under its own `scheduled_delivery` key rather than inside
// `applies_to_modes`. It is kept separate because delivery slots carry
// constraints Pickup and Takeout do not — same-day only, an 8-hour
// ceiling and a 60-minute ordering cutoff — and applying one mode's
// rules to the other is precisely the confusion the split prevents.
//
// Two things this hook must NOT do for delivery, both regressions of the
// parallel ASAP Uber workstream if they slip in:
//   * never mark ASAP delivery `blocked` — delivery is not governed by
//     the catalog's Pickup/Takeout counter hours, and the backend
//     deliberately lets an ASAP delivery order through them;
//   * never offer a delivery date other than today.
//
// And one it MUST do, added Sep 2026 after staging offered windows
// checkout then refused: narrow the delivery window list to the set
// that satisfies the ordering cutoff and the horizon the backend
// advertises in `scheduled_delivery`. The availability endpoint is
// mode-agnostic and gates its windows on the catalog's lead time, which
// is the Pickup/Takeout rule, not delivery's.
//
// Fails OPEN on any error. A flaky network or a 500 must never block a
// customer from ordering: checkout itself is the enforcement point and
// fails closed on its own.

// The delivery cutoff and horizon are NOT restated here.
//
// They were, briefly. That duplicate ran against an anchor frozen at the
// moment the availability response was built (`asap_window.start` minus
// the lead time) and never advanced, so the displayed list stayed still
// while the customer's real clock moved. Staging offered an 8:30 PM
// window, the customer spent three minutes on the address form, and
// checkout refused it under the same 60-minute rule. The two sides never
// disagreed about the rule — only about `now`.
//
// So the server decides, against its own current instant, and this hook
// renders the answer. `mode` is what asks it to: without it the endpoint
// answers for Pickup and Takeout, exactly as before.

// How often a scheduled-delivery storefront re-reads availability while
// the customer is choosing. The server's answer is only true for the
// instant it was asked: a window an hour out at 7:30 is 57 minutes out
// at 7:33 and checkout will refuse it. Re-reading bounds how stale the
// displayed list can be to roughly this interval, instead of to however
// long the customer has had the page open. Delivery only — Pickup and
// Takeout have no cutoff to fall through.
const DELIVERY_REFRESH_MS = 30000;

const EMPTY = {
  loading: false,
  schedulingEnabled: false,
  asapAvailable: true,
  blocked: false,
  earliest: null,
  leadTimeMinutes: null,
  timezone: null,
  reason: null,
  dates: [],
  canSchedule: false,
  scheduledDelivery: { available: false },
  // Bumped on every answer, so a consumer showing a window list knows to
  // re-read it rather than keep rendering the one it already has.
  windowsVersion: 0,
};

// `linkSlug` is the storefront the customer is actually on
// (`/retail/{slug}/{linkSlug}`). It is forwarded so this request resolves
// the SAME catalog the page resolved: the backend honours a catalog_id
// backed by a live link profile without additionally demanding the legacy
// assistant link. Without it a correctly published link-slug catalog
// answered 404 here while browsing fine — and because this hook fails
// OPEN, that 404 became "no scheduling" and the ASAP/scheduled choice
// silently disappeared rather than erroring.
export default function useFulfillmentAvailability({
  slug, catalogId, linkSlug, orderType, enabled = true,
}) {
  const [state, setState] = useState(EMPTY);
  const requestRef = useRef(0);
  /** Backend windows for one date, already narrowed to what checkout
   *  will accept for `orderType`. Never synthesised, never re-filtered
   *  here — see the note above. */
  const fetchWindows = useCallback(async (date) => {
    const { data } = await axios.get(`${RETAIL_BASE}/${slug}/catalog/availability`, {
      params: {
        catalog_id: catalogId, date, mode: orderType,
        ...(linkSlug ? { link_slug: linkSlug } : {}),
      },
    });
    const requested = data?.requested_date;
    return Array.isArray(requested?.windows) ? requested.windows : [];
  }, [slug, catalogId, linkSlug, orderType]);

  const refresh = useCallback(async () => {
    if (!enabled || !slug || !catalogId || !orderType) {
      setState(EMPTY);
      return;
    }
    const requestId = ++requestRef.current;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const { data } = await axios.get(`${RETAIL_BASE}/${slug}/catalog/availability`, {
        params: {
          catalog_id: catalogId, mode: orderType,
          ...(linkSlug ? { link_slug: linkSlug } : {}),
        },
      });
      // A stale response from a mode the customer already switched away
      // from must not overwrite the current one.
      if (requestId !== requestRef.current) return;

      const mode = String(orderType || "").toLowerCase();
      const isDelivery = mode === "delivery";
      const appliesFromList = Array.isArray(data?.applies_to_modes)
        ? data.applies_to_modes.includes(mode)
        : false;
      const scheduledDelivery = data?.scheduled_delivery || { available: false };
      const deliverySchedulable = isDelivery && !!scheduledDelivery.available;
      const applies = appliesFromList || deliverySchedulable;

      const schedulingEnabled = !!data?.scheduling_enabled;
      const asapAvailable = !!data?.asap_available;
      const allDates = Array.isArray(data?.dates) ? data.dates : [];
      // Same-day only for delivery. The backend refuses a non-today slot
      // anyway; trimming here means the customer is never shown a date
      // that checkout would reject.
      const dates = deliverySchedulable ? allDates.slice(0, 1) : allDates;

      // Scheduled delivery only: find out UP FRONT whether any window
      // today survives the ordering cutoff and the horizon. Without
      // this the "Schedule for later" button offers itself and only
      // then reveals an empty list — or, before this filter existed,
      // a list of windows checkout refuses.
      let deliveryWindows = null;
      if (deliverySchedulable && dates.length) {
        deliveryWindows = await fetchWindows(dates[0].date);
        if (requestId !== requestRef.current) return;
      }

      setState((prev) => ({
        loading: false,
        windowsVersion: prev.windowsVersion + 1,
        schedulingEnabled,
        asapAvailable,
        // The only derived values, and each is a conjunction of what the
        // backend already said.
        //
        // `blocked` deliberately excludes delivery: the catalog's
        // Pickup/Takeout hours do not govern ASAP delivery, and marking
        // it blocked here would stop ASAP Uber orders that the backend
        // accepts.
        blocked: appliesFromList && !isDelivery && schedulingEnabled && !asapAvailable,
        // For delivery, a date with windows is not enough: every one of
        // them may sit inside the ordering cutoff.
        canSchedule: applies && schedulingEnabled && dates.some((d) => d.available)
          && (deliveryWindows === null || deliveryWindows.length > 0),
        earliest: data?.earliest_available || null,
        leadTimeMinutes: data?.lead_time_minutes ?? null,
        timezone: data?.timezone || null,
        reason: data?.reason || null,
        dates,
        scheduledDelivery,
      }));
    } catch (_e) {
      if (requestId !== requestRef.current) return;
      setState(EMPTY);
    }
  }, [slug, catalogId, linkSlug, orderType, enabled, fetchWindows]);

  useEffect(() => { refresh(); }, [refresh]);

  // Keep the answer live while scheduled delivery is on offer. Without
  // this the list is only ever as fresh as the moment the modal opened.
  const deliveryLive = !!state.scheduledDelivery?.available
    && String(orderType || "").toLowerCase() === "delivery";
  useEffect(() => {
    if (!enabled || !deliveryLive) return undefined;
    const id = setInterval(() => { refresh(); }, DELIVERY_REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, deliveryLive, refresh]);

  /** Backend-provided windows for one date, as sent. */
  const loadWindows = useCallback(async (date) => {
    if (!slug || !catalogId || !date) return [];
    try {
      return await fetchWindows(date);
    } catch (_e) {
      return [];
    }
  }, [slug, catalogId, fetchWindows]);

  return { ...state, refresh, loadWindows };
}
